import React, { useState, Suspense, lazy } from 'react';
import { DEFAULT_AVATAR, handleAvatarError } from '../utils/imageFallback';
import {
  validateAndDecodeImage,
  cropAndCompress,
  PROFILE_TARGET,
} from '../utils/imageUpload';
import avatar from '../assets/imgs/DigitalContacts-logo.svg';
import { Link, Redirect } from 'react-router-dom';
import firebase from '../config/fbConfig';
import { updateUser } from '../actions/registerUser';
import { connect } from 'react-redux';
import Spinner from './Spinner';
import CropLoadErrorBoundary from './CropLoadErrorBoundary';

// Lazy-loaded: react-easy-crop only needs to be fetched once a user
// actually picks a file, not on every page load.
const CropModal = lazy(() => import('./CropModal'));

const AddProfileImage = ({ updateUser, user, isAuth, loading }) => {
  const [state, setstate] = useState({ avatarURL: DEFAULT_AVATAR });
  const [imgLoading, setimgLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressingLabel, setCompressingLabel] = useState('Compressing photo…');
  const [cropSrc, setCropSrc] = useState(null);
  const [pendingBlob, setPendingBlob] = useState(null);

  const resetCropState = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setPendingBlob(null);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setCompressingLabel('Preparing photo…');
    setCompressing(true);
    let decoded;
    try {
      decoded = await validateAndDecodeImage(file);
    } catch (err) {
      setCompressing(false);
      alert(err.message);
      return;
    }
    setCompressing(false);

    setPendingBlob(decoded);
    setCropSrc(URL.createObjectURL(decoded));
  };

  const handleCropCancel = () => resetCropState();

  const handleCropLoadError = () => {
    resetCropState();
    setCompressing(false);
    alert(
      'Something went wrong loading the photo editor. Please try selecting your photo again.'
    );
  };

  const handleCropConfirm = async (croppedAreaPixels) => {
    const blob = pendingBlob;
    resetCropState();

    setCompressingLabel('Compressing photo…');
    setCompressing(true);
    let prepared = blob;
    try {
      prepared = await cropAndCompress(blob, croppedAreaPixels, PROFILE_TARGET);
    } catch (err) {
      console.warn('Crop/compression failed, uploading original file instead.', err);
    } finally {
      setCompressing(false);
    }

    setimgLoading(true);
    const filename = `avatar_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    try {
      const ref = firebase.storage().ref('images').child(filename);
      await ref.put(prepared);
      const url = await ref.getDownloadURL();
      setstate((prev) => ({ ...prev, avatarURL: url }));
    } catch (err) {
      console.error(err);
      alert('Upload failed, please try again.');
    } finally {
      setimgLoading(false);
    }
  };

  const hasPhoto = state.avatarURL !== DEFAULT_AVATAR;

  const handleUpdateUser = () => {
    updateUser({ avatarUrl: hasPhoto ? state.avatarURL : '' });
  };

  // loading is a third state, distinct from isAuth -- while the initial
  // GET /api/auth/current is still in flight, isAuth reads false (its
  // initial value) even for an already-logged-in visitor. Checking it
  // before loading resolves would redirect a still-logged-in user to
  // /register on every refresh. Checked first, before anything below
  // that reads isAuth/user.
  if (loading || !user) return <Spinner />;

  if (isAuth && user.avatarUrl !== null) {
    return <Redirect to='/bio' />;
  }

  if (!isAuth) {
    return <Redirect to='/register' />;
  }

  return (
      <div className='my-name add-pro-photo'>
        <div className='container'>
          <div className='row'>
            <div className='col-12'>
              <div className='logo'>
                <Link to='/'>
                  <img src={avatar} alt='logo' />
                </Link>
              </div>
              <div
                className='headline'
                style={{ position: 'relative', display: 'inline-block' }}
              >
                <h1 style={{ opacity: '1' }}>
                  Add a selfie so people you cross paths with know its you.
                </h1>
              </div>

              <div id='show1' className='toppad showtxt1'>
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className='upload-btn-wrapper'>
                    {compressing || imgLoading ? (
                      <>
                        <Spinner />
                        <p style={{ textAlign: 'center', fontSize: '12px' }}>
                          {compressing ? compressingLabel : 'Uploading photo…'}
                        </p>
                      </>
                    ) : (
                      <img
                        src={state.avatarURL || DEFAULT_AVATAR}
                        id='tmpImg'
                        alt='logo'
                        onError={handleAvatarError}
                      />
                    )}

                    <button className='btnn'></button>
                    <input
                      type='file'
                      accept='image/*'
                      name='avatar'
                      id='img'
                      onChange={handleFileSelected}
                      disabled={compressing || imgLoading || Boolean(cropSrc)}
                    />
                  </div>
                  <div
                    className='headline'
                    style={{ position: 'relative', display: 'inline-block' }}
                  >
                    <p>
                      Add a profile photo so people you cross paths with know
                      it’s you.
                    </p>
                  </div>

                  <button
                    className='btn w-100prc'
                    id='submitBtn'
                    onClick={handleUpdateUser}
                    disabled={imgLoading || compressing || Boolean(cropSrc)}
                  >
                    {hasPhoto ? 'Next' : 'Skip for now'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        {cropSrc && (
          <CropLoadErrorBoundary onError={handleCropLoadError}>
            <Suspense fallback={<Spinner />}>
              <CropModal
                imageSrc={cropSrc}
                aspect={PROFILE_TARGET.cropAspect}
                cropShape={PROFILE_TARGET.cropShape}
                onCancel={handleCropCancel}
                onConfirm={handleCropConfirm}
              />
            </Suspense>
          </CropLoadErrorBoundary>
        )}
      </div>
    );
};

const mapStateToProps = (state) => ({
  user: state.registerUser.user,
  isAuth: state.registerUser.isAuth,
  loading: state.registerUser.loading,
});

export default connect(mapStateToProps, { updateUser })(AddProfileImage);
